import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import {
  Button,
  PageHeading,
  Icon,
  ConfirmDialog,
  employeeID,   
  companyID, 
} from "../../../Common/UIComponents";
import {
  fetchTestTypes,
  deleteTwoColumnListEntry,
} from "../../../API_Service/Settings_Slice";
import ConfigTable from "../../../Common/Tables/ConfigTable";
import DashboardLayout from "../../../Layout/DashboardLayout";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const buildTableRows = (items = []) =>
  items.map((item, i) => ({
    id:               item.Equipment_Test_Type_ID  ?? `row-${i}`,
    name:             item.Equipment_Test_Type_nm   ?? "Unknown",
    description:      item.TestDescription          ?? "—",
    standard:         item.Basic_Standard_Name      ?? "N/A",
    basicStandardId:  item.Basic_Standard_Id        ?? null,   // numeric ID for edit pre-fill
    facilityIDs:      item.FacilityIDs              ?? "",
    facilities:       (item.FacilityName ?? "").split(",").map((f) => f.trim()).filter(Boolean),
    classification:   item.Classification           ?? "N/A",  // name — for display only
    classificationId: String(item.Test_Classification_Id ?? ""), // ← ID for dropdown pre-fill
  }));

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

const EXPORT_COLUMNS = [
  { key: "name",           title: "Test Type Name"        },
  { key: "description",    title: "Test Type Description" },
  { key: "standard",       title: "Basic Standard"        },
  { key: "facilities",     title: "Facilities"            },
  { key: "classification", title: "Classification"        },
];

// ─────────────────────────────────────────────
// TOOLTIP
// ─────────────────────────────────────────────

const Tooltip = ({ children, content, maxW = "max-w-xs" }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos]         = useState({ top: 0, left: 0, flip: false });
  const triggerRef            = useRef(null);

  const show = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const flip = rect.top > window.innerHeight / 2;
    setPos({ top: flip ? rect.top - 8 : rect.bottom + 8, left: rect.left + rect.width / 2, flip });
    setVisible(true);
  };

  return (
    <span ref={triggerRef} className="relative inline-block" onMouseEnter={show} onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && (
        <div className={`fixed z-[9999] pointer-events-none ${maxW} w-max bg-[#181c1d] text-white text-xs rounded-lg px-3 py-2 shadow-xl leading-relaxed -translate-x-1/2 ${pos.flip ? "-translate-y-full" : ""}`}
          style={{ top: pos.top, left: pos.left }}>
          {content}
          {pos.flip
            ? <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-[#181c1d]" />
            : <div className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-[#181c1d]" />
          }
        </div>
      )}
    </span>
  );
};

// ─────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────

const StatsRow = ({ rows }) => {
  const total = rows.length;
  const facilitySet = useMemo(() => { const ids = new Set(); rows.forEach((r) => (r.facilities ?? []).forEach((f) => ids.add(f))); return ids.size; }, [rows]);
  const standardSet = useMemo(() => new Set(rows.map((r) => r.standard).filter(Boolean)).size, [rows]);

  return (
    <div className="grid grid-cols-3 gap-4 mb-5">
      <div className="bg-white p-4 rounded-xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/50">
        <div className="flex justify-between items-start mb-2">
          <div className="p-2 bg-[#005b60]/10 rounded-lg text-[#005b60]"><span className="material-symbols-outlined text-[20px]">biotech</span></div>
        </div>
        <p className="text-[#3e494a] text-xs font-medium">Total Test Types</p>
        <h3 className="text-xl font-bold mt-0.5">{total}</h3>
      </div>
      <div className="bg-white p-4 rounded-xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/50">
        <div className="flex justify-between items-start mb-2">
          <div className="p-2 bg-[#005683]/10 rounded-lg text-[#005683]"><span className="material-symbols-outlined text-[20px]">corporate_fare</span></div>
        </div>
        <p className="text-[#3e494a] text-xs font-medium">Mapped Facilities</p>
        <h3 className="text-xl font-bold mt-0.5">{facilitySet}</h3>
      </div>
      <div className="bg-white p-4 rounded-xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/50">
        <div className="flex justify-between items-start mb-2">
          <div className="p-2 bg-[#bbf46e]/20 rounded-lg text-[#436900]"><span className="material-symbols-outlined text-[20px]">verified</span></div>
        </div>
        <p className="text-[#3e494a] text-xs font-medium">Active Standards</p>
        <h3 className="text-xl font-bold mt-0.5">{standardSet}</h3>
      </div>
    </div>
  );
};

const ClassificationBadge = ({ value }) => {
  const map = { Clinical: "bg-[#bbf46e] text-[#112000]", Advanced: "bg-cyan-100 text-cyan-700", Critical: "bg-red-100 text-red-700" };
  const cls = map[value] ?? "bg-[#e0e3e3] text-[#3e494a]";
  return <span className={`px-3 py-1 rounded-full text-[10px] font-extrabold uppercase ${cls}`}>{value ?? "Standard"}</span>;
};

const FacilityStack = ({ facilities = [] }) => {
  const colors  = ["bg-[#cce5ff]", "bg-[#9bf0f8]", "bg-[#e0e3e3]", "bg-[#bbf46e]"];
  const visible = facilities.slice(0, 2);
  const extra   = facilities.length - 2;
  if (!facilities.length) return <span className="text-xs text-[#3e494a]">—</span>;
  const list = (
    <ul className="space-y-1 text-left min-w-[140px]">
      {facilities.map((f, i) => (
        <li key={i} className="flex items-center gap-2 whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full bg-[#7fd4db] flex-shrink-0" />{f}
        </li>
      ))}
    </ul>
  );
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex -space-x-2">
        {visible.map((f, i) => (
          <Tooltip key={i} content={f}>
            <div className={`w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-bold cursor-default select-none ${colors[i % colors.length]}`}>
              {f.slice(0, 2).toUpperCase()}
            </div>
          </Tooltip>
        ))}
      </div>
      {extra > 0 && (
        <Tooltip content={list} maxW="max-w-sm">
          <div className="w-7 h-7 rounded-full bg-[#e0e3e3] border-2 border-white flex items-center justify-center text-[10px] font-bold text-[#3e494a] cursor-pointer hover:bg-[#005b60]/20 transition-colors select-none">
            +{extra}
          </div>
        </Tooltip>
      )}
    </div>
  );
};

const DescriptionCell = ({ text }) => {
  const MAX = 45;
  const isTruncated = text && text !== "—" && text.length > MAX;
  const display = isTruncated ? text.slice(0, MAX) + "…" : (text || "—");
  if (!isTruncated) return <span className="text-sm text-[#3e494a]">{display}</span>;
  return (
    <Tooltip content={<span className="leading-relaxed max-w-[280px] block">{text}</span>} maxW="max-w-sm">
      <span className="text-sm text-[#3e494a] cursor-default">{display}</span>
    </Tooltip>
  );
};

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────

export default function TestTypeConfigIndex({ embedded = false } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [testTypes,     setTestTypes]     = useState([]);
  const [isLoading,     setIsLoading]     = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError,   setDeleteError]   = useState(null);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [currentPage,   setCurrentPage]   = useState(1);
  const [pageSize,      setPageSize]      = useState(10);
  const [deleteDialog,  setDeleteDialog]  = useState({ open: false, row: null });
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [editSuccess,   setEditSuccess]   = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setSettingsError(null);
    dispatch(fetchTestTypes({ ID: 0, companyID: companyID }))
      .then((res) => setTestTypes(toArray(res)))
      .catch((err) => setSettingsError(err?.message ?? "Failed to load test types"))
      .finally(() => setIsLoading(false));
  }, [location.key]);

  useEffect(() => {
  if (location.state?.editSuccess) {
    setEditSuccess(true);
    setTimeout(() => setEditSuccess(false), 3000);
    navigate(location.pathname, { replace: true, state: {} });
  }
}, [location.state?.editSuccess]);

  const rows = useMemo(() => buildTableRows(testTypes), [testTypes]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        (r.standard ?? "").toLowerCase().includes(q) ||
        (r.classification ?? "").toLowerCase().includes(q) ||
        (r.facilities ?? []).some((f) => f.toLowerCase().includes(q))
    );
  }, [searchQuery, rows]);

  const totalPages    = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const handleEdit = useCallback(
    (row) => navigate("/application-config/test-type/create", { state: { mode: "edit", data: row } }),
    [navigate]
  );
  const handleDelete       = useCallback((row) => setDeleteDialog({ open: true, row }), []);
  const handleCancelDelete = useCallback(() => { setDeleteDialog({ open: false, row: null }); setDeleteBlocked(false); }, []);

  const handleConfirmDelete = useCallback(() => {
    const { row } = deleteDialog;
    if (!row) return;
    setDeleteLoading(true);
    setDeleteError(null);
    dispatch(deleteTwoColumnListEntry({ term: "TESTTYPE", id: row.id, modifiedBy: employeeID, companyID: companyID }))
      .then((action) => {
        const result = action?.payload?.data;
        if (Number(result) === -1) {
          setDeleteBlocked(true);
          return;
        }
        setDeleteBlocked(false);
        setDeleteDialog({ open: false, row: null });
        setDeleteSuccess(true);
        dispatch(fetchTestTypes({ ID: 0, companyID: companyID }))
          .then((res) => setTestTypes(toArray(res)));
        setTimeout(() => setDeleteSuccess(false), 3000);
      })
      .catch((err) => setDeleteError(err?.message ?? "Failed to delete"))
      .finally(() => setDeleteLoading(false));
  }, [deleteDialog, dispatch]);

  const handleAddNew = useCallback(
    () => navigate("/application-config/test-type/create"),
    [navigate]
  );
  const handleSearchChange   = useCallback((e) => { setSearchQuery(e.target.value); setCurrentPage(1); }, []);
  const handlePageSizeChange = useCallback((size) => { setPageSize(size); setCurrentPage(1); }, []);

  const columns = useMemo(() => [
    {
      key: "name", title: "Test Type Name",
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#005b60]/10 flex items-center justify-center text-[#005b60] font-bold text-sm">
            {row.name.charAt(0).toUpperCase()}
          </div>
          <span className="font-bold text-[#181c1d]">{row.name}</span>
        </div>
      ),
    },
    { key: "description",    title: "Test Type Description", render: (row) => <DescriptionCell text={row.description} /> },
    { key: "standard",       title: "Basic Standard",        render: (row) => <span className="bg-[#e5e9e9] px-2 py-1 rounded text-xs font-mono text-[#181c1d]">{row.standard}</span> },
    { key: "facilities",     title: "Facilities",            render: (row) => <FacilityStack facilities={row.facilities} /> },
    { key: "classification", title: "Classification",        render: (row) => <ClassificationBadge value={row.classification} /> },
  ], []);

  const inner = (
    <>
      <div className="px-8 py-4 bg-[#f7fafa] text-[#181c1d] min-h-screen">



        <div className="flex flex-wrap justify-between items-end mb-3 gap-4">
          <PageHeading
            title="Test Type Management"
            subtitle="Define and manage test types used across your laboratory. Organize test classifications to maintain consistency in reporting and workflows."
          />
          <Button variant="primary" onClick={handleAddNew}>
            <Icon name="add" className="mr-1 text-[20px]" />
            Add New Test Type
          </Button>
        </div>

        <StatsRow rows={rows} />

        <ConfigTable
          persistKey="application-config-testtype"
          columns={columns}
          rows={isLoading ? [] : paginatedRows}
          onEdit={handleEdit}
          onDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          allFilteredRows={filteredRows}
          exportColumns={EXPORT_COLUMNS}
          exportFilename="test_types"
          emptyText={isLoading ? "Loading…" : "No test types found."}
        />

        {settingsError && (
          <div className="mt-6 rounded-xl bg-red-50 text-red-700 p-6 text-sm">{settingsError}</div>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteDialog.open}
        variant="danger"
        isLoading={deleteLoading}
        errorMessage={deleteBlocked ? "This test type is already mapped to a project. Please remove the mapping before deleting." : null}
        cancelLabel={deleteBlocked ? "OK, Got it" : "Cancel"}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {deleteSuccess && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold" role="status">
          <Icon name="check_circle" className="text-[20px]" filled />
          Test Type Deleted successfully.
        </div>
      )}
{editSuccess && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-semibold" role="status">
          <Icon name="check_circle" className="text-[20px]" filled />
          Test Type Updated successfully.
        </div>
      )}
      {deleteError && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-red-600 text-white text-sm font-semibold px-6 py-3 rounded-xl shadow-lg">
          {deleteError}
        </div>
      )}
    </>
  );
  return embedded ? inner : <DashboardLayout>{inner}</DashboardLayout>;
}

